import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token,
        new_password: password,
      });
      localStorage.setItem("glam_token", data.token);
      await refresh?.();
      toast.success("Password updated. Welcome back.");
      navigate(data.user?.profile_complete ? "/studio" : "/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0E13] text-[#F7F5F2] flex items-center justify-center p-8">
      <div className="w-full max-w-md fade-up">
        <Link to="/" className="flex items-center gap-2 mb-12" data-testid="reset-logo-link">
          <Sparkles className="w-4 h-4 text-[#E3C2B0]" strokeWidth={1.5} />
          <span className="font-serif text-2xl">StyleMe</span>
        </Link>

        <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">Reset password</span>
        <h1 className="font-serif text-5xl mt-4 font-light leading-tight">Choose a new one.</h1>

        {!token ? (
          <p className="mt-8 text-[#A8A6A1] leading-relaxed">
            This reset link is missing its token. Please open the link from your email again.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-10 space-y-8">
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] block mb-3">
                New password
              </label>
              <input
                type="password"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password"
                className="w-full bg-transparent border-b border-white/15 focus:border-[#E3C2B0] outline-none py-3 text-[#F7F5F2] transition-colors text-lg font-light"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] block mb-3">
                Confirm password
              </label>
              <input
                type="password"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                data-testid="reset-confirm"
                className="w-full bg-transparent border-b border-white/15 focus:border-[#E3C2B0] outline-none py-3 text-[#F7F5F2] transition-colors text-lg font-light"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit"
              className="w-full bg-[#E3C2B0] text-[#0A0908] py-4 rounded-sm hover:bg-[#F0D4C5] transition-colors text-xs uppercase tracking-[0.3em] disabled:opacity-50"
            >
              {loading ? "Updating..." : "Update password"}
            </button>
          </form>
        )}

        <Link
          to="/auth?mode=login"
          data-testid="reset-back-link"
          className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#A8A6A1] hover:text-[#F7F5F2]"
        >
          <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
